// Distribución de Herramientas — vistas "por pañol" y "por persona".
// Lista con contador + link a detalle; no muestra detalles inline.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Package, User, ArrowRight, Loader2, Search, Factory, Truck, AlertCircle, Printer, FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';
import { exportToCSV, printTable } from '../../lib/printUtils';

const TIPO_COLOR = {
  matriz: 'bg-indigo-100 text-indigo-800',
  faena: 'bg-emerald-100 text-emerald-800',
  movil: 'bg-amber-100 text-amber-800',
};
const TIPO_ICON = { matriz: Factory, faena: Package, movil: Truck };

export default function DistribucionHerramientasPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('panoles');
  const [panoles, setPanoles] = useState({ items: [], sin_panol_count: 0, stock_matriz_disponible: 0, total_asignadas: 0 });
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [qPersona, setQPersona] = useState('');

  const loadPanoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/bodega/distribucion/por-panol');
      setPanoles(res.data || { items: [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando distribución');
    } finally { setLoading(false); }
  }, []);

  const loadPersonas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/bodega/distribucion/por-persona${qPersona ? `?q=${encodeURIComponent(qPersona)}` : ''}`);
      setPersonas(res.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando distribución');
    } finally { setLoading(false); }
  }, [qPersona]);

  useEffect(() => {
    if (tab === 'panoles') loadPanoles();
    else if (tab === 'personas') loadPersonas();
  }, [tab, loadPanoles, loadPersonas]);

  // Carlos (10/05/2026): imprimir y exportar Distribución según tab activo
  const imprimirDistribucion = () => {
    const isPanol = tab === 'panoles';
    const rows = isPanol ? (panoles.items || []) : personas;
    if (!rows.length) { toast.warning(`Sin datos en ${isPanol ? 'pañoles' : 'personas'} para imprimir`); return; }
    printTable({
      title: `Distribución de Herramientas · ${isPanol ? 'Por Pañol' : 'Por Persona'}`,
      subtitle: `${rows.length} ${isPanol ? 'pañoles' : 'personas'} · ${new Date().toLocaleString('es-CL')}`,
      summary: isPanol ? [
        { label: 'Total pañoles', value: rows.length },
        { label: 'Total asignadas', value: panoles.total_asignadas },
        { label: 'Stock matriz disponible', value: panoles.stock_matriz_disponible },
        { label: 'Sin pañol', value: panoles.sin_panol_count },
      ] : [
        { label: 'Personas con herramientas', value: rows.length },
        { label: 'Total ítems', value: rows.reduce((s, p) => s + (p.total_herramientas || 0), 0) },
      ],
      columns: isPanol ? [
        { key: 'codigo', label: 'Código', mono: true, align: 'center' },
        { key: 'nombre', label: 'Pañol' },
        { key: 'tipo', label: 'Tipo', align: 'center', format: (v) => (v || '').toUpperCase() },
        { key: 'responsable_nombre', label: 'Responsable' },
        { key: 'ot_asociada', label: 'OT', align: 'center' },
        { key: 'total_herramientas', label: 'Herram.', align: 'right' },
      ] : [
        { key: 'nombre', label: 'Persona' },
        { key: 'rut', label: 'RUT', mono: true },
        { key: 'cargo', label: 'Cargo' },
        { key: 'panol_codigo', label: 'Pañol', align: 'center' },
        { key: 'total_herramientas', label: 'Herram.', align: 'right' },
      ],
      rows,
    });
  };

  const exportarDistribucionExcel = () => {
    const isPanol = tab === 'panoles';
    const rows = isPanol ? (panoles.items || []) : personas;
    if (!rows.length) { toast.warning('Sin datos para exportar'); return; }
    exportToCSV({
      filename: `distribucion_${isPanol ? 'panoles' : 'personas'}`,
      title: `CHIRINO STEEL · Distribución Herramientas · ${isPanol ? 'Por Pañol' : 'Por Persona'}`,
      summary: [
        { label: 'Tab', value: isPanol ? 'Por Pañol' : 'Por Persona' },
        { label: 'Total registros', value: rows.length },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: isPanol ? [
        { key: 'codigo', label: 'Código pañol' },
        { key: 'nombre', label: 'Nombre pañol' },
        { key: 'tipo', label: 'Tipo' },
        { key: 'ubicacion_geo', label: 'Ubicación' },
        { key: 'responsable_nombre', label: 'Responsable' },
        { key: 'responsable_rut', label: 'RUT responsable' },
        { key: 'ot_asociada', label: 'OT asociada' },
        { key: 'total_herramientas', label: 'Total herramientas', format: (v) => Number(v) || 0 },
      ] : [
        { key: 'nombre', label: 'Nombre' },
        { key: 'rut', label: 'RUT' },
        { key: 'cargo', label: 'Cargo' },
        { key: 'panol_codigo', label: 'Pañol' },
        { key: 'total_herramientas', label: 'Total herramientas', format: (v) => Number(v) || 0 },
      ],
      rows,
    });
  };

  return (
    <div className="container mx-auto p-3 md:p-6 space-y-4" data-testid="page-distribucion">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Package className="w-6 h-6 text-indigo-600" /> Distribución de Herramientas
        </h1>
        <p className="text-sm text-muted-foreground">
          Vista agregada de dónde están todas las herramientas asignadas. Click en cualquier fila para ver detalle.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button variant={tab === 'panoles' ? 'default' : 'outline'} onClick={() => setTab('panoles')} data-testid="tab-dist-panoles">
          <Package className="w-4 h-4 mr-1" /> Por Pañol
        </Button>
        <Button variant={tab === 'personas' ? 'default' : 'outline'} onClick={() => setTab('personas')} data-testid="tab-dist-personas">
          <User className="w-4 h-4 mr-1" /> Por Persona
        </Button>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={imprimirDistribucion} title="Imprimir tab actual" data-testid="btn-print-distribucion">
            <Printer className="w-4 h-4 mr-1" /> Imprimir
          </Button>
          <Button variant="outline" size="sm" onClick={exportarDistribucionExcel} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel" data-testid="btn-excel-distribucion">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : tab === 'panoles' ? (
        <PanolesTab data={panoles} navigate={navigate} />
      ) : (
        <PersonasTab
          personas={personas}
          q={qPersona}
          setQ={setQPersona}
          onSearch={loadPersonas}
        />
      )}
    </div>
  );
}

function PanolesTab({ data, navigate }) {
  return (
    <>
      {/* Resumen */}
      <div className="grid grid-cols-3 gap-2">
        <Card><CardContent className="p-3">
          <div className="text-[10px] uppercase text-muted-foreground">Asignadas</div>
          <div className="text-2xl font-bold text-indigo-700">{data.total_asignadas}</div>
          <div className="text-[10px] text-muted-foreground">En cargo</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-[10px] uppercase text-muted-foreground">En Matriz</div>
          <div className="text-2xl font-bold text-emerald-700">{data.stock_matriz_disponible}</div>
          <div className="text-[10px] text-muted-foreground">Disponibles</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-[10px] uppercase text-muted-foreground">Sin pañol</div>
          <div className="text-2xl font-bold text-amber-700">{data.sin_panol_count}</div>
          <div className="text-[10px] text-muted-foreground">Por asignar</div>
        </CardContent></Card>
      </div>

      {/* Listado pañoles */}
      <Card>
        <CardContent className="p-0">
          {data.items.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">Sin pañoles activos</div>
          ) : (
            <div className="divide-y">
              {data.items.map((p) => {
                const TipoIcon = TIPO_ICON[p.tipo] || Package;
                return (
                  <button
                    key={p.panol_id}
                    onClick={() => navigate(`/bodega/panoles/${p.panol_id}`)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-900/50 text-left transition"
                    data-testid={`row-dist-panol-${p.panol_id}`}
                  >
                    <TipoIcon className="w-5 h-5 text-slate-500" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                        {p.nombre}
                        <Badge className={`text-[9px] h-4 ${TIPO_COLOR[p.tipo] || ''}`}>{p.tipo}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {p.responsable_nombre ? (
                          <>{p.responsable_nombre} · {p.responsable_tipo}</>
                        ) : (
                          <span className="italic text-amber-600">sin responsable</span>
                        )}
                        {p.ot_asociada > 0 && <> · OT {p.ot_asociada}</>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-indigo-700 tabular-nums">{p.total_herramientas}</div>
                      <div className="text-[10px] text-muted-foreground">herramientas</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                );
              })}
              {data.sin_panol_count > 0 && (
                <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-950/30" data-testid="row-sin-panol">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">⚠️ Sin pañol asignado</div>
                    <div className="text-xs text-muted-foreground italic">
                      Herramientas en cargo sin pañol — requieren asignación manual
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-amber-700 tabular-nums">{data.sin_panol_count}</div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function PersonasTab({ personas, q, setQ, onSearch }) {
  return (
    <>
      <form
        onSubmit={(e) => { e.preventDefault(); onSearch(); }}
        className="flex gap-2"
      >
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Buscar persona…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="input-persona-search"
          />
        </div>
        <Button type="submit" size="sm">Buscar</Button>
      </form>

      <Card>
        <CardContent className="p-0">
          {personas.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">
              {q ? (
                <>Sin resultados para &quot;{q}&quot;</>
              ) : (
                <div className="space-y-2">
                  <div>Sin personas con herramientas asignadas.</div>
                  <div className="text-xs italic max-w-md mx-auto">
                    Las herramientas importadas del Access aún no tienen pañol/persona asignada.
                    Reasignalas desde <strong>Bodega → Herramientas en Cargo → Asignar</strong>.
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {personas.map((p, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-900/50"
                  data-testid={`row-dist-persona-${idx}`}
                >
                  <User className="w-5 h-5 text-slate-500" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{p.nombre}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {p.rut && <span>{p.rut} · </span>}
                      Pañoles: {p.panoles_asociados.join(' · ')}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-emerald-700 tabular-nums">{p.total_herramientas}</div>
                    <div className="text-[10px] text-muted-foreground">herramientas</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <div className="text-xs text-muted-foreground text-center">
        {personas.length} personas · {personas.reduce((acc, p) => acc + p.total_herramientas, 0)} herramientas asignadas
      </div>
    </>
  );
}
